import { useQuery } from '@apollo/client';
import { Divider, ListSubheader, Menu, MenuItem, useTheme } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import { Check, KeyboardArrowDown } from '@material-ui/icons';
import * as React from 'react';

import { zIndexes } from '../../common/css-variables';
import { AgentConversation, GET_AGENT_CONVERSATIONS } from '../../graphql/queries/agent';
import { useAgent } from '../../hooks/use-agent';
import { useSession } from '../../hooks/use-session';
import { conversationDropdownMenuStyles, conversationDropdownStyles } from './agent-page.styles';

const MENU_PAPER_STYLE: React.CSSProperties = {
    borderRadius: 8,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    marginTop: 4,
    maxHeight: 400,
    width: 320
};

interface GroupedConversations {
    today: AgentConversation[];
    previous7Days: AgentConversation[];
    previous30Days: AgentConversation[];
}

const MS_PER_DAY = 86400000;
const DAYS_IN_WEEK = 7;
const DAYS_IN_MONTH = 30;

function groupConversationsByTime(conversations: AgentConversation[]): GroupedConversations {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const sevenDaysAgo = todayStart - DAYS_IN_WEEK * MS_PER_DAY;
    const thirtyDaysAgo = todayStart - DAYS_IN_MONTH * MS_PER_DAY;

    const groups: GroupedConversations = {
        previous30Days: [],
        previous7Days: [],
        today: []
    };

    for (const conversation of conversations) {
        const timestamp = conversation.lastMessageAt;
        if (timestamp >= todayStart) {
            groups.today.push(conversation);
        } else if (timestamp >= sevenDaysAgo) {
            groups.previous7Days.push(conversation);
        } else if (timestamp >= thirtyDaysAgo) {
            groups.previous30Days.push(conversation);
        }
    }

    return groups;
}

export const ConversationDropdown: React.FC = () => {
    const theme = useTheme<Theme>();
    const session = useSession();
    const userId = session?.user?.id;
    const { conversationId, onSelectConversation } = useAgent();

    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const isOpen = Boolean(anchorEl);

    const { data, refetch } = useQuery<{ agent_conversations: AgentConversation[] }>(GET_AGENT_CONVERSATIONS, {
        pollInterval: 60000,
        skip: !userId,
        variables: { userId }
    });

    // Re-fetch when conversation ID changes
    React.useEffect(() => {
        if (conversationId) {
            refetch();
        }
    }, [conversationId, refetch]);

    const conversations = data?.agent_conversations || [];
    const grouped = groupConversationsByTime(conversations);

    const currentConversation = conversations.find((c) => c.id === conversationId);
    const triggerText = currentConversation?.title || 'Past conversations';

    const handleTriggerClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSelectConversation = (event: React.MouseEvent<HTMLElement>) => {
        const id = event.currentTarget.dataset.conversationId;
        if (id) {
            onSelectConversation(id);
            handleClose();
        }
    };

    const renderSection = (title: string, items: AgentConversation[], showDivider: boolean) => {
        if (items.length === 0) {
            return null;
        }

        const menuItems = items.map((conversation) => {
            const isSelected = conversation.id === conversationId;
            const checkIcon = isSelected ? <Check className="conversation-dropdown__checkIcon" /> : null;
            return (
                <MenuItem
                    key={conversation.id}
                    data-conversation-id={conversation.id}
                    onClick={handleSelectConversation}
                    selected={isSelected}
                    className="conversation-dropdown__item"
                >
                    <span className="conversation-dropdown__itemText">{conversation.title || 'New chat'}</span>
                    {checkIcon}
                </MenuItem>
            );
        });

        const divider = showDivider ? <Divider className="conversation-dropdown__divider" /> : null;

        return (
            <React.Fragment key={title}>
                {divider}
                <ListSubheader className="conversation-dropdown__sectionHeader" disableSticky={true}>
                    {title}
                </ListSubheader>
                {menuItems}
            </React.Fragment>
        );
    };

    const hasToday = grouped.today.length > 0;
    const hasPrevious7Days = grouped.previous7Days.length > 0;
    const hasPrevious30Days = grouped.previous30Days.length > 0;
    const hasGroupedConversations = hasToday || hasPrevious7Days || hasPrevious30Days;
    const menuContent = hasGroupedConversations ? (
        <>
            {renderSection('Today', grouped.today, false)}
            {renderSection('Previous 7 days', grouped.previous7Days, hasToday)}
            {renderSection('Previous 30 days', grouped.previous30Days, hasToday || hasPrevious7Days)}
        </>
    ) : (
        <MenuItem disabled={true} className="conversation-dropdown__empty">
            No previous chats
        </MenuItem>
    );

    const chevronClassName = `conversation-dropdown__chevron${isOpen ? ' is-open' : ''}`;

    return (
        <div css={conversationDropdownStyles(theme)}>
            <button type="button" className="conversation-dropdown__trigger" onClick={handleTriggerClick}>
                <span className="conversation-dropdown__triggerText">{triggerText}</span>
                <KeyboardArrowDown className={chevronClassName} />
            </button>
            <Menu
                anchorEl={anchorEl}
                open={isOpen}
                onClose={handleClose}
                getContentAnchorEl={null}
                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                style={{ zIndex: zIndexes.modalDialog }}
                PaperProps={{ style: MENU_PAPER_STYLE }}
                css={conversationDropdownMenuStyles(theme)}
            >
                {menuContent}
                <Divider className="conversation-dropdown__divider" />
                <MenuItem
                    component="a"
                    href="/orbit"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="conversation-dropdown__viewAllLink"
                    onClick={handleClose}
                >
                    View all conversations
                </MenuItem>
            </Menu>
        </div>
    );
};
